import { SERVICE_CATEGORIES } from '@powerful/config/categories';
import type { SearchResult } from '@powerful/data';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { formatDistance, formatPrice, formatRating } from '../../lib/discovery/format';
import type { PublicFeedBusiness } from '../../lib/discovery/public-feed-cache';
import { Icon } from '../icons/icon';

/** O que o card precisa — satisfeito pelo resultado da Edge e pelo snapshot offline público. */
export type CardBusiness = PublicFeedBusiness & { distanceKm?: number | null };

export interface BusinessCardProps {
  business: CardBusiness;
  /** Explicação do ranking (C7). Ausente no snapshot offline: sem dados reais, sem botão. */
  ranking?: SearchResult['ranking'];
  onExplain?: () => void;
  /** Sem origem a distância é «—» (C4) — nunca um número inventado. */
  hasOrigin: boolean;
}

/** Menos de 10 avaliações → «Novo»: a média de 3 opiniões engana mais do que informa. */
export const MIN_REVIEWS_FOR_RATING = 10;

export function categorySlug(categoryId: string | null): string | null {
  return SERVICE_CATEGORIES.find((c) => c.id === categoryId)?.slug ?? null;
}

export function initials(name: string): string {
  const words = name.replace(/-/g, ' ').split(/\s+/).filter((w) => /^\p{Lu}/u.test(w));
  return words.slice(0, 2).map((w) => w[0]).join('') || name.slice(0, 1).toUpperCase();
}

/**
 * Card de resultado — canvas `CardAnatomy`.
 *
 * Um único link (o nome, esticado sobre o card por `::after`) e ações IRMÃS acima dele, nunca
 * interativos aninhados (C8). O `--metal` só aparece na crista do Selo; o preço é tinta dourada
 * (`--gold-text`, 4,5:1). Favoritar e agendar não aparecem: T2.14/M3 — nada de botão inerte.
 * Distintivos de plano são neutros (P2B, art. 7.º); mérito («Top avaliado») usa `badge-gold`.
 */
export function BusinessCard({ business: b, ranking, onExplain, hasOrigin }: BusinessCardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const slug = categorySlug(b.categoryId);
  const category = slug ? t(`onboarding.categories.${slug}`) : null;
  const distance = hasOrigin ? formatDistance(b.distanceKm ?? null, locale) : '—';
  const noDistance = distance === '—';
  const sealed = b.sealStatus === 'approved' || b.badges.includes('seal');
  const planBadge = b.badges.includes('premium_powerful') ? 'premium_powerful' : b.badges.includes('verified_powerful') ? 'verified_powerful' : null;

  return (
    <article className="card pc-bcard" data-testid="business-card">
      <div className="pc-cover" aria-hidden="true">
        {b.coverUrl ? <img src={b.coverUrl} alt="" loading="lazy" decoding="async" /> : <Icon name="image" size={36} strokeWidth={1.4} />}
        {category ? <span className="pc-cover-cat badge badge-neutral">{category}</span> : null}
      </div>
      <div className="pc-bcard-body">
        <div className="pc-bhead">
          <span className="pc-logo" aria-hidden="true">
            {b.logoUrl ? <img src={b.logoUrl} alt="" loading="lazy" decoding="async" /> : initials(b.name)}
          </span>
          <h3 className="pc-bname">
            <Link className="pc-stretch" to={`/pro/${encodeURIComponent(b.slug)}`}>{b.name}</Link>
          </h3>
        </div>
        <p className="pc-meta">
          {[category, b.city].filter(Boolean).join(' · ')}
          {category || b.city ? ' · ' : null}
          <span className="pc-nowrap">{distance}</span>
          {noDistance ? <span className="pc-vh"> ({t('discovery.card.noDistance')})</span> : null}
        </p>
        <div className="pc-row">
          {b.ratingCount >= MIN_REVIEWS_FOR_RATING ? (
            <span className="pc-rating">
              <Icon name="star" size={15} />
              <strong>{formatRating(b.ratingAvg, locale)}</strong>
              <span>· {t('discovery.card.reviews', { count: b.ratingCount })}</span>
            </span>
          ) : (
            <span className="badge badge-neutral" title={t('discovery.card.newHint')}>
              {t('discovery.card.new')}
              <span className="pc-vh"> — {t('discovery.card.newHint')}</span>
            </span>
          )}
        </div>
        {b.minPriceCents !== null ? (
          <div className="pc-price-row">
            <span>
              <span className="pc-from">{t('discovery.card.from')} </span>
              <span className="pc-price">{formatPrice(b.minPriceCents, locale)}</span>
            </span>
          </div>
        ) : null}
        {sealed || planBadge || b.badges.includes('top_rated') ? (
          <div className="pc-badges">
            {sealed ? (
              <span className="selo pc-selo-sm">
                <span className="crest"><Icon name="crown" size={14} strokeWidth={2} /></span>
                <span className="txt"><span className="a">{t('discovery.card.seal')}</span></span>
              </span>
            ) : null}
            {b.badges.includes('top_rated') ? <span className="badge badge-gold">{t('discovery.badges.top_rated')}</span> : null}
            {planBadge ? <span className="badge badge-neutral">{t(`discovery.badges.${planBadge}`)}</span> : null}
          </div>
        ) : null}
        {ranking && onExplain ? (
          <button className="hit pc-why" type="button" aria-haspopup="dialog" onClick={onExplain}>
            <Icon name="info" size={15} />
            <span>{t('discovery.ranking.why')}</span>
            <span className="pc-vh">: {b.name}</span>
          </button>
        ) : null}
      </div>
    </article>
  );
}
